import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Heading,
  Input,
  IconButton,
  HStack,
  SimpleGrid,
  Text,
  Tag,
  TagLabel,
  Button,
  useDisclosure,
  useColorMode,
  useOutsideClick,
} from "@chakra-ui/react";
import { CloseIcon } from "@chakra-ui/icons";
import { FiFilter } from "react-icons/fi";
import axiosInstance from "../../helpers/axiosInstance"; // Adjust the path according to your axios instance
import banner from "../../assets/images/banner.jpg";
import Translate from "../../components/LanguageTranslation/Translate";
import { useTranslation } from "../../components/LanguageTranslation/TranslationContext";

const BlogBanner = ({ selectedFilters, setSelectedFilters }) => {
  const { useTranslate } = useTranslation();
    const placeholderText = useTranslate("To search");
  const { colorMode } = useColorMode();
  const { isOpen, onToggle, onOpen, onClose } = useDisclosure(); // Add onOpen and onClose
  const [categories, setCategories] = useState([]);
  const [searchInput, setSearchInput] = useState(""); // State for search input

  const popupRef = useRef();

  // Close when clicked outside
  useOutsideClick({
    ref: popupRef,
    handler: () => {
      if (isOpen) onToggle();
    },
  });

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axiosInstance.get("/categories/");
        if (response.data.success) {
          setCategories(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching categories", error);
      }
    };

    fetchCategories();
  }, []);

  // Open popup when search input changes and is not empty
  useEffect(() => {
    if (searchInput) {
      onOpen();
    } else {
      onClose();
    }
  }, [searchInput, onOpen, onClose]);

  const handleFilterChange = (category, value) => {
    const updatedFilters = { ...selectedFilters };
    if (updatedFilters[category]?.includes(value)) {
      updatedFilters[category] = updatedFilters[category].filter(
        (v) => v !== value
      );
      if (updatedFilters[category].length === 0) {
        delete updatedFilters[category];
      }
    } else {
      if (!updatedFilters[category]) updatedFilters[category] = [];
      updatedFilters[category].push(value);
    }
    setSelectedFilters(updatedFilters);
    // console.log("selectedFilters", selectedFilters);
  };

  // Filter categories based on search input
  const filteredCategories = categories.map((category) => ({
    ...category,
    category_value_list: category.category_value_list.filter((value) =>
      value.toLowerCase().includes(searchInput.toLowerCase())
    ),
  }));

  // --------- just below useState / inside the component ----------
  const getSelectedValues = () =>
    Object.entries(selectedFilters) //  [ ['Country', ['Italy']], … ]
      .flatMap(
        (
          [category, arr] //  -> ['Italy', …]
        ) => arr.map((value) => ({ category, value }))
      );

  return (
    <Box
      bgImage={`url(${banner})`} // Add your banner image path here
      bgSize="cover"
      bgPosition="bottom"
      w="100%"
      h="60vh"
      px={{ base: 4, md: 12 }}
      pt={0}
      color={colorMode === "light" ? "black" : "white"}
      position="relative" // Ensure relative position to allow absolute child positioning
      display="flex"
      flexDirection="column"
      justifyContent="center" // Center vertically
      alignItems="center" // Center horizontally
      fontFamily="Poppins, sans-serif"
    >
      <Box textAlign="center" w="100%">
        {/* Banner Heading */}
        <Heading
          as="h1"
          fontSize={{ base: "3xl", md: "6xl" }}
          mb={6}
          color="white"
          textShadow="1px 1px 2px rgba(0, 0, 0, 0.5)"
        >
          <Translate text="Travel Blogs" />
        </Heading>

        {/* Search bar and filter icon */}
        <HStack
          spacing={1}
          p={1}
          w={{ base: "80%", md: "80%", lg: "50%" }}
          border="1px solid"
          borderRadius="md"
          borderColor="white"
          boxShadow="md"
          mx="auto"
          bg="blackAlpha.300"
          overflowX="auto" // 👈 allow horizontal scroll on small screens
        >
          {/* ───── selected filter chips ───── */}
          {getSelectedValues().map(({ category, value }) => (
            <Tag
              key={`${category}-${value}`}
              size="sm"
              borderRadius="full"
              bg="#286328"
              color="white"
              pr={1}
              pl={3}
            >
              <TagLabel>{value}</TagLabel>
              {/* optional close‑button so user can deselect right here */}
              <IconButton
                aria-label="Remove filter"
                icon={<CloseIcon w={2} h={2} />}
                size="xs"
                m={0}
                ml={1}
                variant="ghost"
                color="white"
                _hover={{ bg: "transparent" }}
                onClick={() => handleFilterChange(category, value)}
              />
            </Tag>
          ))}

          {/* ───── search text input ───── */}
          <Input
            placeholder={getSelectedValues().length ? "" : placeholderText }
            variant="unstyled"
            _placeholder={{ color: "gray.50" }}
            textColor="white"
            px={4}
            flex="1"
            size="sm"
            value={searchInput}
            onClick={onToggle}
            onChange={(e) => setSearchInput(e.target.value)}
          />

          {/* filter icon stays the same */}
          <IconButton
            icon={<FiFilter />}
            size="lg"
            aria-label="Filter"
            onClick={onToggle}
            variant="unstyled"
            color="white"
          />
        </HStack>
      </Box>

      {/* Categories Popup */}
      {isOpen && (
        <Box
          ref={popupRef} // 👈 Add this line
          position="absolute" // Use absolute positioning to overlay without pushing content
          top="68%" // Ensure it appears just below the filter button
          left="50%" // Center horizontally
          transform="translateX(-50%)" // Centering trick for absolute positioning
          w="80%" // Cover 80% of the screen width
          bg={colorMode === "light" ? "white" : "gray.700"}
          boxShadow="xl"
          p={6}
          mt={2}
          borderRadius="lg"
          zIndex={10} // Ensure it appears on top
        >
          {/* Close Button */}
          <IconButton
            aria-label="Close popup"
            icon={<CloseIcon />}
            position="absolute"
            borderRadius="full"
            boxShadow="lg"
            top={2}
            right={5}
            variant="ghost"
            onClick={onToggle}
          />

          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
            {filteredCategories.map(
              (category) =>
                category.category_value_list.length > 0 && ( // Only show categories with results
                  <Box key={category.category_name}>
                    <Text fontWeight="bold" mb={4}>
                      <Translate text={category.category_name} />
                    </Text>
                    <HStack wrap="wrap">
                      {category.category_value_list.map((value) => (
                        <Tag
                          key={`${category.category_name}-${value}`}
                          size="lg"
                          // borderRadius="full"
                          variant={
                            selectedFilters[category.category_name]?.includes(
                              value
                            )
                              ? "solid"
                              : "outline"
                          }
                          bg={
                            selectedFilters[category.category_name]?.includes(
                              value
                            )
                              ? "#286328"
                              : "none"
                          }
                          cursor="pointer"
                          onClick={() =>
                            handleFilterChange(category.category_name, value)
                          }
                        >
                          <TagLabel>
                            <Translate text={value} />
                          </TagLabel>
                        </Tag>
                      ))}
                    </HStack>
                  </Box>
                )
            )}
          </SimpleGrid>
          <HStack justify="space-between" mt={8}>
            <Button onClick={() => setSelectedFilters({})} variant="outline">
              <Translate text="Clear Filters" />
            </Button>
            <Button
              onClick={onToggle}
              bg="black"
              color="white"
              transition="transform 0.3s"
              _hover={{
                transform: "scale(1.1)",
              }}
            >
              <Translate text=" Apply Filters" />
            </Button>
          </HStack>
        </Box>
      )}
    </Box>
  );
};

export default BlogBanner;
